import { Button } from '@creditornot/cb-button';
import { Spinner, Typography as T } from '@creditornot/cb-ingredients';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useFeatureFlags } from 'common/hooks';

import { KybProcessContext } from './KybProcessContext';
import { reportContinueAfterBusinessVerificationSubmittedForReview } from './behavioral';
import GirlDesktop from '../../../common/illustrations/GirlDesktop';
import { CenteredContent, Heading, IllustrationContainer, TextStack } from '../ComplianceContent';
import { BREAKPOINT_MOBILE } from '../breakpoints';

const StyledBody = styled(T.Body1)`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 600px;
`;

const StyledButton = styled(Button)`
  width: 100%;
  @media (min-width: ${BREAKPOINT_MOBILE}px) {
    max-width: 200px;
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-block-start: 50px;
`;

type Keys = {
  heading: string;
  body: string;
  button: string;
};

const BusinessVerificationInformationSubmitted = () => {
  const history = useHistory();

  const { countryCode } = useContext(KybProcessContext);

  const { data } = useFeatureFlags(['venue_data_collection'], { country: countryCode });

  if (!data) {
    return (
      <SpinnerContainer>
        <Spinner size={'large'} />
      </SpinnerContainer>
    );
  }

  const isVenueDataCollectionEnabled = data?.venue_data_collection === 'on';

  const handleContinue = () => {
    if (isVenueDataCollectionEnabled) {
      reportContinueAfterBusinessVerificationSubmittedForReview();
    }
    history.push('/');
  };

  let keys: Keys;
  if (isVenueDataCollectionEnabled) {
    keys = {
      heading: 'compliance.kyb.information-submitted.information-submitted',
      body: 'compliance.kyb.information-submitted.reviewing-your-documents',
      button: 'compliance.kyb.success.continue',
    };
  } else {
    keys = {
      heading: 'compliance.kyb.information-submitted.information-submitted',
      body: 'compliance.kyb.information-submitted.body',
      button: 'compliance.kyb.success.back-to-front-page',
    };
  }

  return (
    <CenteredContent>
      <IllustrationContainer>
        <GirlDesktop width="100%" />
      </IllustrationContainer>

      <TextStack>
        <Heading>
          <FormattedMessage id={keys.heading} />
        </Heading>

        <StyledBody>
          <div>
            <FormattedMessage id={keys.body} />
          </div>
          {isVenueDataCollectionEnabled && (
            <div>
              <FormattedMessage id="compliance.kyb.success.next-steps" />
            </div>
          )}
        </StyledBody>
      </TextStack>

      <StyledButton size="large" variant="blue" onClick={handleContinue}>
        <FormattedMessage id={keys.button} />
      </StyledButton>
    </CenteredContent>
  );
};

export default BusinessVerificationInformationSubmitted;
